import React, { useEffect, useRef, useState } from "react";
import {
  DatePicker, Input, Select, Table, Tag, Tooltip,
} from "antd";
import dayjs from "dayjs";
import moment from "moment";
import { toast } from "react-toastify";
import { fetchRcaTracking } from "../../../../actions/qc";
import { getUserList } from "../../../../actions/sendReports";
import useAbortController from "../../../Common/hooks/useAbortController";
import { formatAntdDateRange } from "../../../../utils/helpers";
import RcaHistoryModal from "../RcaHistoryModal";
import RcaAssignToSelect from "../../DataOpsAnalytics/RcaAssignToSelect";
import { formatAddRcaTableRcaStatus } from "../../DataOpsAnalytics/utils/addRcaTestSamplesUtils";
import { loadDataOpsAssigneeSelectOptions } from "../../DataOpsAnalytics/utils/utils";
import {
  enrichRcaRowWithMockFields,
  getRcaStatusTagColor,
  matchesRcaAssignToFilter,
  RCA_STATUS_FILTER_OPTIONS,
} from "../../DataOpsAnalytics/utils/rcaUtils";
import "../QC.css";
import "./RcaTracking.css";

const { RangePicker } = DatePicker;
const dateFormat = "MM/DD/YYYY";

const defaultDateRange = () => [
  dayjs().subtract(29, "day"),
  dayjs(),
];

const legacyStatusFilterOptions = [
  { value: "", label: "All" },
  { value: 1, label: "Active" },
  { value: 2, label: "Completed" },
];

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
};

const formatDisplayDateTime = (value) => {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY, h:mm a") : value;
};

const getStatusTagColor = (rcaStatus) => {
  if (rcaStatus === 1) return "processing";
  if (rcaStatus === 2) return "success";
  return "default";
};

const getLegacyStatusLabel = (rcaStatus) => {
  if (rcaStatus === 1) return "Active";
  if (rcaStatus === 2) return "Completed";
  return "Unknown";
};

const DEFAULT_PAGE_SIZE = 50;
const DATA_OPS_DEFAULT_PAGE_SIZE = 100;

/** Stable Ant Design row key — one row per RCA (latest list). */
const mapRcaTrackingListRow = (item, index) => {
  const rcaKey = item.rca_pk ?? item.rca_id ?? item.id;
  const rowKey = rcaKey != null && rcaKey !== ""
    ? `rca-${rcaKey}`
    : `rca-tis-${item.tis_pk ?? "unknown"}-${index}`;
  return enrichRcaRowWithMockFields({
    ...item,
    key: rowKey,
    customer_name: item.customer_company ?? item.customer_name ?? item.customer_id,
    test_category: item.test_category ?? item.category_name ?? item.test_category_name,
    test_name: item.test_name ?? item.analysis,
    sample_id: item.sample_id ?? item.sample_id_display,
  });
};

const RcaTracking = ({
  variant = "qc",
  filterActions = null,
  onRegisterRefresh = null,
}) => {
  const isDataOpsVariant = variant === "dataOps";
  const statusFilterOptions = isDataOpsVariant
    ? RCA_STATUS_FILTER_OPTIONS
    : legacyStatusFilterOptions;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [updatedByFilter, setUpdatedByFilter] = useState("");
  const [assignToFilter, setAssignToFilter] = useState("");
  const [userFilterOptions, setUserFilterOptions] = useState([]);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [dateRange, setDateRange] = useState(() => defaultDateRange());
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [paginationConfig, setPaginationConfig] = useState({
    current: 1,
    pageSize: isDataOpsVariant ? DATA_OPS_DEFAULT_PAGE_SIZE : DEFAULT_PAGE_SIZE,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["25", "50", "100"],
  });
  const isMountedRef = useRef(true);
  const { setNewAbortController } = useAbortController();

  useEffect(() => {
    const loadUserFilterOptions = async () => {
      const response = await getUserList();
      if (!isMountedRef.current) return;
      if (!response.success) {
        toast.error(response.message || "Failed to fetch user list");
        return;
      }
      const userOptions = (response.results || [])
        .slice()
        .sort((userA, userB) => {
          const nameA = `${userA.first_name} ${userA.last_name}`.trim().toLowerCase();
          const nameB = `${userB.first_name} ${userB.last_name}`.trim().toLowerCase();
          return nameA.localeCompare(nameB);
        })
        .map((user) => ({
          value: user.id,
          label: `${user.first_name} ${user.last_name}`.trim(),
        }));
      setUserFilterOptions(userOptions);
    };

    const loadAssigneeOptions = async () => {
      if (!isDataOpsVariant) {
        return;
      }
      const response = await loadDataOpsAssigneeSelectOptions();
      if (!isMountedRef.current) return;
      if (!response.success) {
        toast.error(response.message || "Failed to load Assign To users");
        return;
      }
      setAssigneeOptions(response.options);
    };

    loadUserFilterOptions();
    loadAssigneeOptions();
  }, [isDataOpsVariant]);

  const fetchList = async () => {
    setLoading(true);
    const abortController = setNewAbortController();
    const params = {
      search: search || "",
      filter: "latest",
    };
    if (statusFilter !== "") {
      if (isDataOpsVariant) {
        params.status = Number(statusFilter);
      } else {
        params.status = statusFilter;
      }
    }
    if (isDataOpsVariant && assignToFilter !== "") {
      params.assigned_to = assignToFilter;
    }
    if (createdByFilter !== "") {
      params.created_by = createdByFilter;
    }
    if (updatedByFilter !== "") {
      params.updated_by = updatedByFilter;
    }
    // API expects MM/DD/YYYY-MM/DD/YYYY (no spaces), e.g. 07/01/2026-07/12/2026
    const formattedDateRange = formatAntdDateRange(dateRange);
    if (formattedDateRange) {
      params.date_range = formattedDateRange;
    } else if (!search) {
      params.date_range = formatAntdDateRange(defaultDateRange());
    }

    const {
      success, result, message, canceled,
    } = await fetchRcaTracking(params, abortController.signal);

    if (!isMountedRef.current || canceled) {
      setLoading(false);
      return;
    }

    if (success) {
      const resultRows = Array.isArray(result) ? result : [];
      let mappedRows = resultRows.map((item, index) => mapRcaTrackingListRow(item, index));
      if (isDataOpsVariant && assignToFilter) {
        mappedRows = mappedRows.filter((row) => matchesRcaAssignToFilter(row, assignToFilter));
      }
      setRows(mappedRows);
      setPaginationConfig((prev) => ({
        ...prev,
        current: 1,
        total: mappedRows.length,
      }));
      setSelectedRecord((previous) => {
        if (!previous) {
          return previous;
        }
        const previousRcaKey = previous.rca_pk ?? previous.rca_id ?? previous.id;
        const match = mappedRows.find((row) => {
          const rowRcaKey = row.rca_pk ?? row.rca_id ?? row.id;
          if (previousRcaKey != null && rowRcaKey != null) {
            return String(rowRcaKey) === String(previousRcaKey);
          }
          return row.tis_pk != null && String(row.tis_pk) === String(previous.tis_pk);
        });
        return match ?? previous;
      });
    } else {
      toast.error(message);
      setRows([]);
      setPaginationConfig((prev) => ({
        ...prev,
        current: 1,
        total: 0,
      }));
    }
    setLoading(false);
  };

  const fetchListRef = useRef(fetchList);
  fetchListRef.current = fetchList;

  useEffect(() => {
    isMountedRef.current = true;
    fetchList();
    return () => {
      isMountedRef.current = false;
    };
  }, [search, statusFilter, createdByFilter, updatedByFilter, assignToFilter, dateRange, isDataOpsVariant]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onRegisterRefresh) {
      // Parent keeps one callback; ref always points at latest fetchList (current filters).
      onRegisterRefresh(() => fetchListRef.current());
    }
  }, [onRegisterRefresh]);

  const handlePageChange = (page, pageSize) => {
    setPaginationConfig((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
  };

  const openDetail = (record) => {
    setSelectedRecord(record);
  };

  const closeDetail = () => {
    setSelectedRecord(null);
  };

  const handleAssignToUpdated = (updatedRecord) => {
    if (isDataOpsVariant) {
      fetchList();
      return;
    }

    setRows((previousRows) => previousRows.map((row) => (
      row.key === updatedRecord.key
      || row.id === updatedRecord.id
      || row.rca_id === updatedRecord.rca_id
        ? enrichRcaRowWithMockFields({ ...row, ...updatedRecord, key: row.key })
        : row
    )));
    setSelectedRecord((previous) => {
      if (!previous) {
        return previous;
      }
      if (previous.key !== updatedRecord.key
        && previous.id !== updatedRecord.id
        && previous.rca_id !== updatedRecord.rca_id) {
        return previous;
      }
      return enrichRcaRowWithMockFields({ ...previous, ...updatedRecord, key: previous.key });
    });
  };

  const dateColumnCellProps = {
    onHeaderCell: () => ({ className: "RcaTracking__TableCol RcaTracking__TableCol--date" }),
    onCell: () => ({ className: "RcaTracking__TableCol RcaTracking__TableCol--date" }),
  };

  const columns = [
    {
      title: "WO",
      dataIndex: "workorder_id",
      key: "workorder_id",
      ...(isDataOpsVariant ? { width: 120, ellipsis: true } : {}),
    },
    {
      title: "Genista ID",
      dataIndex: "genista_id",
      key: "genista_id",
      ...(isDataOpsVariant ? { width: 120, ellipsis: true } : {}),
    },
    {
      title: "Analysis",
      dataIndex: "analysis",
      key: "analysis",
      ...(isDataOpsVariant ? { width: 140, ellipsis: true } : {}),
    },
    {
      title: "Status",
      dataIndex: "rca_status",
      key: "rca_status",
      ...(isDataOpsVariant ? { width: 120 } : {}),
      render: (rcaStatus, record) => {
        if (isDataOpsVariant) {
          const label = formatAddRcaTableRcaStatus(rcaStatus, record);
          if (label === "-") {
            return label;
          }
          return (
            <Tag color={getRcaStatusTagColor(rcaStatus, record)}>
              {label}
            </Tag>
          );
        }
        return (
          <Tag color={getStatusTagColor(rcaStatus)}>{getLegacyStatusLabel(rcaStatus)}</Tag>
        );
      },
    },
    ...(isDataOpsVariant ? [{
      title: "Assign To",
      dataIndex: "assigned_to",
      key: "assigned_to",
      width: 180,
      render: (_value, record) => (
        <RcaAssignToSelect
          record={record}
          assigneeOptions={assigneeOptions}
          onUpdated={handleAssignToUpdated}
        />
      ),
    }] : []),
    {
      title: "Expected",
      dataIndex: "expected_complete_date",
      key: "expected_complete_date",
      width: 112,
      ...dateColumnCellProps,
      render: formatDisplayDate,
    },
    {
      title: "Completed",
      dataIndex: "actual_complete_date",
      key: "actual_complete_date",
      width: 112,
      ...dateColumnCellProps,
      render: formatDisplayDate,
    },
    {
      title: "Created By",
      dataIndex: "created_by_name",
      key: "created_by_name",
      ...(isDataOpsVariant ? {
        width: 120,
        ellipsis: true,
        onHeaderCell: () => ({ className: "RcaTracking__TableCol RcaTracking__TableCol--createdBy" }),
        onCell: () => ({ className: "RcaTracking__TableCol RcaTracking__TableCol--createdBy" }),
      } : {}),
      render: (value, record) => (
        <Tooltip title={`Created At: ${formatDisplayDateTime(record.created_at)}`}>
          <span>{value || "-"}</span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="QCContainer RcaTracking">
      <div className="RcaTracking__Filters">
        <div className="RcaTracking__FilterField">
          <label>Search</label>
          <Input.Search
            className="RcaTracking__Search"
            allowClear
            placeholder="Search with WO, GB ID"
            value={searchInput}
            onChange={(event) => {
              const value = event.target.value;
              setSearchInput(value);
              if (value === "") {
                setSearch("");
              }
            }}
            onSearch={(value) => setSearch(value || "")}
          />
        </div>
        <div className="RcaTracking__FilterField">
          <label>Status</label>
          <Select
            className="RcaTracking__StatusFilter"
            allowClear
            options={statusFilterOptions}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value ?? "")}
            onClear={() => setStatusFilter("")}
          />
        </div>
        {isDataOpsVariant && (
          <div className="RcaTracking__FilterField">
            <label>Assign To</label>
            <Select
              className="RcaTracking__UserFilter"
              showSearch
              allowClear
              options={[{ value: "", label: "All" }, ...assigneeOptions]}
              value={assignToFilter}
              onChange={(value) => setAssignToFilter(value ?? "")}
              filterOption={(inputValue, option) => (
                option?.label?.toLowerCase().includes(inputValue?.toLowerCase())
              )}
              listHeight={200}
            />
          </div>
        )}
        <div className="RcaTracking__FilterField">
          <label>Created By</label>
          <Select
            className="RcaTracking__UserFilter"
            showSearch
            allowClear
            options={[{ value: "", label: "All" }, ...userFilterOptions]}
            value={createdByFilter}
            onChange={(value) => setCreatedByFilter(value ?? "")}
            onClear={() => setCreatedByFilter("")}
            filterOption={(inputValue, option) => (
              option?.label?.toLowerCase().includes(inputValue?.toLowerCase())
            )}
            listHeight={200}
          />
        </div>
        <div className="RcaTracking__FilterField">
          <label>Updated By</label>
          <Select
            className="RcaTracking__UserFilter"
            showSearch
            allowClear
            options={[{ value: "", label: "All" }, ...userFilterOptions]}
            value={updatedByFilter}
            onChange={(value) => setUpdatedByFilter(value ?? "")}
            onClear={() => setUpdatedByFilter("")}
            filterOption={(inputValue, option) => (
              option?.label?.toLowerCase().includes(inputValue?.toLowerCase())
            )}
            listHeight={200}
          />
        </div>
        <div className="RcaTracking__FilterField">
          <label>Date Range</label>
          <RangePicker
            className="RcaTracking__DateRange"
            format={dateFormat}
            value={dateRange}
            onChange={(value) => setDateRange(value || defaultDateRange())}
            allowClear
            placeholder={["Start date", "End date"]}
          />
        </div>
        {filterActions ? (
          <div className="RcaTracking__FilterActions">
            {filterActions}
          </div>
        ) : null}
      </div>

      <Table
        className="QCTable RcaTracking__Table"
        rowKey="key"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ ...paginationConfig, onChange: handlePageChange }}
        scroll={{
          x: isDataOpsVariant ? "max-content" : true,
          y: "60vh",
        }}
        tableLayout={isDataOpsVariant ? "fixed" : undefined}
        locale={{ emptyText: "No Results" }}
        onRow={(record) => ({
          onClick: () => openDetail(record),
          className: "QCTableRow",
        })}
      />

      <RcaHistoryModal
        open={Boolean(selectedRecord)}
        onCancel={closeDetail}
        tisPk={selectedRecord?.tis_pk}
        workorderId={selectedRecord?.workorder_id}
        genistaId={selectedRecord?.genista_id}
        analysis={selectedRecord?.analysis}
        variant={isDataOpsVariant ? "dataOps" : "qc"}
        assigneeOptions={isDataOpsVariant ? assigneeOptions : userFilterOptions}
        onAssigneeUpdated={handleAssignToUpdated}
        parentRecord={selectedRecord}
        onRcaDetailsUpdated={fetchList}
      />
    </div>
  );
};

export default RcaTracking;
